/**
 * One line of the Publish Readiness checklist (design-system.md §6 "Checklist
 * row"; `.checkitem` in the prototype's contest-overview.css).
 *
 * Two states, and they differ by more than colour: a done step is inert, an
 * outstanding one carries the link to go and fix it. The design leans on that —
 * the card's whole job in draft is to be the actionable thing on the screen.
 */
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { Theme } from '@mui/material/styles';
import { CheckIcon, WarnIcon } from './line-icons';

interface ChecklistRowProps {
  label: string;
  /** The detail under the label — the value when done, what to do when not. */
  hint?: string;
  done: boolean;
  /** Omit for a step with nowhere to send the operator; the CTA is then hidden. */
  onConfigure?: () => void;
  configureLabel?: string;
}

export const ChecklistRow = ({
  label,
  hint,
  done,
  onConfigure,
  configureLabel = 'Configure',
}: ChecklistRowProps) => (
  <Box
    sx={(theme: Theme) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '11px 12px',
      borderRadius: `${theme.tokens.radius.insetRow}px`,
      transition: 'background 0.12s',
      '&:hover': { backgroundColor: theme.tokens.componentColor.rowHover },
    })}
  >
    <Box
      // The glyph is the only thing carrying the done/not-done distinction, so
      // it is labelled rather than hidden: without this a screen reader reads
      // five identical lines and no indication of which are outstanding.
      role="img"
      aria-label={`${label}: ${done ? 'done' : 'not done'}`}
      sx={(theme: Theme) => ({
        display: 'grid',
        placeItems: 'center',
        width: '22px',
        height: '22px',
        flexShrink: 0,
        borderRadius: '50%',
        backgroundColor: done ? theme.tokens.color.successSoft : theme.tokens.color.warnSoft,
        color: done ? theme.tokens.color.success : theme.tokens.color.warn,
      })}
    >
      {done ? <CheckIcon sx={{ fontSize: '14px' }} /> : <WarnIcon sx={{ fontSize: '14px' }} />}
    </Box>

    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        component="span"
        sx={(theme: Theme) => ({
          fontSize: '14px',
          fontWeight: 500,
          // An outstanding step reads at full ink; a done one recedes a shade.
          color: done ? theme.tokens.color.ink2 : theme.tokens.color.ink,
        })}
      >
        {label}
      </Box>
      {hint ? (
        <Box
          component="span"
          sx={(theme: Theme) => ({ fontSize: '12.5px', color: theme.tokens.color.ink4 })}
        >
          {hint}
        </Box>
      ) : null}
    </Box>

    {!done && onConfigure ? (
      <ButtonBase
        onClick={onConfigure}
        aria-label={`${configureLabel} ${label}`}
        sx={(theme: Theme) => ({
          flexShrink: 0,
          padding: '4px 10px',
          borderRadius: `${theme.tokens.radius.badge}px`,
          fontFamily: theme.tokens.font.sans,
          fontSize: '13px',
          fontWeight: 500,
          color: theme.tokens.color.accent,
          '&:hover': { backgroundColor: theme.tokens.color.accentSoft },
        })}
      >
        {configureLabel} →
      </ButtonBase>
    ) : null}
  </Box>
);
